import { Box, Grid, IconButton, InputAdornment } from '@mui/material'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

/** TRANSLATION*/
import { useTranslation } from 'react-i18next'

/** COMPONENT */
import HeaderCard from 'component/CardCustom/HeaderCard'
import Loading from 'component/Loading'
import TableCustom from 'component/Table/TableCustom'
import TableRowCommon from 'component/Table/TableRowCommon'

/** CONSTANTS */
import { selectMe } from 'app/slice/user.slice'
import { Icons } from 'constants/images'
import { colors } from 'constants/theme'
import { routeName } from 'routes/routes-name'

/** API */
import ComponentApi, { Bookmark } from 'api/components-api'
import ReportApi, { FindAllReportInterface, REPORT_GROUP } from 'api/report.api'

/** STYLE */
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { notiError } from 'component/notifications/notifications'
import { Config } from 'config'
import _ from 'lodash'
import MainButton from 'new-components/buttons/main-button'
import SelectInput from 'new-components/inputs/select-input'
import TextInput from 'new-components/inputs/text-input'

export default function Report() {
  const { t } = useTranslation()
  const user = useSelector(selectMe)
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [pageLimit, setPageLimit] = useState(10)
  const [rowCount, setRowCount] = useState(0)
  const [sortBy, setSortBy] = useState('createAt')
  const [sortType, setSortType] = useState('ASC')
  const [search, setSearch] = useState('')
  const [reportGroup, setReportGroup] = useState<REPORT_GROUP | string>('ALL')

  const [reports, setReports] = useState([])


  const loadData = async () => {
    setLoading(true)
    try {
      const condition: FindAllReportInterface = {
        page,
        pageLimit,
        sortType,
        sortBy,
        search,
        reportGroup: reportGroup === 'ALL' ? '' : reportGroup
      }

      ReportApi.reportList(condition)
        .then(({ data, headers }) => {
          setReports(data)
          setRowCount(headers['x-total'])
        })
        .catch((e) => setLoading(false))
        .finally(() => setLoading(false))
    } catch (error) {
      notiError(t('ERROR'))
    }
  }

  useEffect(() => {
    loadData()
  }, [page, pageLimit, sortBy, sortType])

  const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
    setSortBy(sortByVal)
    setSortType(sortTypeVal)
  }

  const handleChangeRowsPerPage = (limit: number) => {
    setLoading(true)
    setPageLimit(limit)
  }

  const handleChangePage = (val: number) => {
    if (page !== val) {
      setLoading(true)
      setPage(val)
    }
  }

  const handleChangeReportGroup = (group: string) => {
    setReportGroup(group)
  }

  const handleBookmark = async (itemId: number, bookmarkId: number) => {
    setLoading(true)
    const payload: Bookmark = {
      bookmarkType: 'REPORT',
      userId: user.userId,
      itemId: itemId,
      isBookmark: bookmarkId ? '0' : '1'
    }
    ComponentApi.bookmark(payload)
      .then((_) => loadData())
      .catch(() => setLoading(false))
      .finally(() => setLoading(false))
  }

  const handleSearch = () => {
    setPage(1)
    loadData()
  }


  const headCells = [
    { id: 'no', disablePadding: false, label: t('TABLE.NO'), width: '100px' },
    { id: 'message', disablePadding: false, label: t('REPORT.TABLE.CODE'), width: '140px' },
    { id: 'message', disablePadding: false, label: t('REPORT.TABLE.TYPE'), width: '20%' },
    { id: 'message', disablePadding: false, label: t('REPORT.TABLE.NAME') },
    { id: 'action', disablePadding: false, label: 'Action', width: '110px' }
  ]

  const renderData = (objData: any, no: number) => {
    const { reportId, reportGroup: reportGroupName, reportUri, reportName, reportNameEn, isBookmark, reportCode, reportKey } = objData

    return (
      <TableRowCommon
        key={reportId.toString()}
        id={reportId.toString()}
        obj={objData}
        columns={[
          { option: 'TEXT', align: 'center', label: page * pageLimit - pageLimit + no + 1, class: 'cursor-pointer' },
          { option: 'TEXT', align: 'left', label: reportCode || '', class: 'text-ellipsis cursor-pointer' },
          { option: 'TEXT', align: 'left', label: t(`REPORT.REPORT_GROUP.${reportGroupName}`) || '-', class: 'text-ellipsis cursor-pointer' },
          { option: 'TEXT', align: 'left', label: t(`REPORT.REPORT_NAME.${reportKey}`) || reportName || reportNameEn || '-', class: 'text-ellipsis cursor-pointer' },
          {
            option: 'COMPONENT',
            align: 'center',
            component: (
              <>
                <IconButton
                  onClick={() => {
                    window.open(`${routeName.report}/${reportUri}`, '_blank')
                  }}
                  className={`p-0 mr-1 align-middle`}
                  sx={{ backgroundColor: 'transparent !important' }}
                >
                  <VisibilityIcon sx={{ fontSize: 23, color: colors.themeSecondColor }} />
                </IconButton>
                <IconButton onClick={() => handleBookmark(reportId, isBookmark)} className={`p-0 ml-2 align-middle`} sx={{ backgroundColor: 'transparent !important' }}>
                  {isBookmark ? <Icons.StarActive width="20" height="20" /> : <Icons.StarInActive width="20" height="20" />}
                </IconButton>
              </>
            )
          }
        ]}
        onClick={() => {
          window.open(`${routeName.report}/${reportUri}`, '_blank')
        }}
      />
    )
  }
  return (
    <Box>
      <HeaderCard text={t('REPORT.HEADER')} />
      <Box paddingX="24px" paddingTop="16px">
        <Grid container spacing="16px">
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <TextInput
              label={t('REPORT.SEARCH')}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value)
                setPage(1)
              }}
              fullWidth
              inputProps={{
                endAdornment: (
                  <InputAdornment position="end" >
                    {!search ? <FontAwesomeIcon icon={faSearch} style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 15 }} /> :
                      <FontAwesomeIcon
                        icon={faTimes}
                        onClick={() => {
                          setSearch('');
                        }}
                        style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 17, cursor: 'pointer' }} />}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <SelectInput
              onChange={(event) => {
                const value = event.target.value
                if (value) {
                  handleChangeReportGroup(value)
                  setPage(1)
                }
              }}
              options={[
                { value: 'ALL', name: t('STATUS.ALL') },
                ..._.filter(
                  Object.values(REPORT_GROUP),
                  (group: string) =>
                    group !== REPORT_GROUP.TDH || Config.IS_TDH === '1'
                ).map((group: string) => ({
                  value: group,
                  name: t(`REPORT.REPORT_GROUP.${group}`),
                }))
              ]}
              label={t('REPORT.FILTER.REPORT_GROUP')}
              value={reportGroup}
              width="100%"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} xl={2}>
            <MainButton
              title={t('INPUT.SEARCH')}
              type='primary'
              variant='contained'
              style={{ width: '120px' }}
              onClick={() => {
                handleSearch()
              }} />
          </Grid>
        </Grid>
        <Box paddingTop="24px">
          <TableCustom
            page={page}
            pageLimit={pageLimit}
            sortType={sortType}
            sortBy={sortBy}
            onSort={onRequestSort}
            setPageLimit={handleChangeRowsPerPage}
            setPage={handleChangePage}
            rowCount={rowCount}
            headCells={headCells}
            rowsData={_.map([...reports], (report, index) => {
              return renderData(report, index)
            })}
            tableFixedWidth
            tableMinWidth={960}
          />
        </Box>
      </Box>
      <Loading show={loading} type="fullLoading" />
    </Box>
  )
}
